import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { billingApi } from '@/api/ops';
import type { PriceExplanation, ProjectionBody, ProjectionItem } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { Select, Segmented } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { TextField } from '@/components/ops/Forms';
import { KeyValues, Section } from '@/components/ops/Layout';
import { MonthSelect } from '@/components/ops/Pickers';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { RatedLinesTable, downloadCsv, ratedLinesCsv } from '@/components/ops/RatedLines';
import { formatCredits, scaleText } from '@/ops/credits';
import { currentPeriod, formatBps, formatMinor, formatNumber, shortId } from '@/ops/format';
import { LIVE } from '@/ops/live';
import { useCreditScale } from '@/ops/useCreditScale';

/**
 * The tenant Costs tab (plan §6.2): the month's rated breakdown — credits first, the money equivalent
 * at the current pack price, our cost and margin — plus the projection calculator, which runs the SAME
 * line builder over assumed volumes. Everything here is a live billing call (CP2).
 */
export function CostsTab({ env, tenantId }: { env: string; tenantId: string }) {
  const [period, setPeriod] = useState(currentPeriod());
  const rating = useQuery({
    queryKey: ['ops', 'billing', env, 'rating', tenantId, period],
    queryFn: () => billingApi.rating(env, tenantId, period),
    ...LIVE,
  });

  return (
    <RequirePermission permission={PERMISSIONS.costsRead} what="Costs">
      <div className="stack">
        <Section
          title="Rated usage"
          meta="live from billing"
          actions={
            <>
              <MonthSelect value={period} onChange={setPeriod} />
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={!rating.data || rating.isError}
                onClick={() => rating.data && downloadCsv(`costs_${tenantId}_${period}.csv`, ratedLinesCsv(rating.data))}
              >
                Export CSV
              </button>
            </>
          }
          flush
        >
          <div className="card-body">
            <QueryState query={rating} service="billing">
              {(usage) => (
                <>
                  <p className="t-caption muted" style={{ margin: '0 0 12px' }}>
                    {usage.period} · {usage.currency} · 1 credit = {scaleText(usage.credit_display_scale)} micro-credits.
                    Money is the equivalent at the current pack price; our cost and margin never reach the tenant.
                  </p>
                  <RatedLinesTable usage={usage} caption={`Rated usage ${usage.period}`} />
                </>
              )}
            </QueryState>
          </div>
        </Section>

        <ExplainSku env={env} tenantId={tenantId} />
        <ProjectionCalculator env={env} tenantId={tenantId} />
      </div>
    </RequirePermission>
  );
}

/** "Why does this tenant pay X for this SKU?" — the resolver's own answer (override → offer → book → default). */
function ExplainSku({ env, tenantId }: { env: string; tenantId: string }) {
  const [sku, setSku] = useState('');
  const [result, setResult] = useState<PriceExplanation | null>(null);
  const { scale } = useCreditScale(env);
  // A read on demand: gcTime 0 so the price never outlives this component in the mutation cache.
  const explain = useMutation({
    mutationFn: () => billingApi.explain(env, tenantId, sku.trim()),
    onSuccess: setResult,
    gcTime: 0,
  });

  const r = result?.resolution;
  const isCredits = r?.currency === 'CREDITS';

  return (
    <Section title="Resolved price of a SKU" meta="the explanation billing's one resolver returns">
      <div className="row-end">
        <div style={{ flex: '1 1 280px' }}>
          <TextField label="SKU" value={sku} onChange={setSku} mono placeholder="credits.pack.communications.10000" />
        </div>
        <button type="button" className="btn btn-ghost" disabled={!sku.trim() || explain.isPending} onClick={() => explain.mutate()}>
          {explain.isPending ? 'Resolving…' : 'Explain'}
        </button>
      </div>
      <MutationError error={explain.error} service="billing" />
      {r && (
        <div style={{ marginTop: 12 }}>
          <KeyValues rows={[
            { label: 'Amount', value: isCredits ? `${formatCredits(r.amount, scale)} (${r.amount.toLocaleString('en-US')} micro-credits)` : formatMinor(r.amount, r.currency) },
            { label: 'Source', value: <StatusChip status={r.source} tone="info" /> },
            { label: 'Explanation', value: r.explanation },
            {
              label: 'Base · discount',
              value: isCredits
                ? `${formatCredits(r.base_amount, scale)} · ${formatCredits(r.discount_amount, scale)}`
                : `${formatMinor(r.base_amount, r.currency)} · ${formatMinor(r.discount_amount, r.currency)}`,
            },
            ...(r.markup_bps !== null ? [{ label: 'Markup', value: formatBps(r.markup_bps) }] : []),
            ...(r.cost_credits !== null ? [{ label: 'Cost', value: formatCredits(r.cost_credits, scale) }] : []),
            ...(r.bonus_credits ? [{ label: 'Bonus credits', value: formatCredits(r.bonus_credits, scale) }] : []),
            ...(r.free_months ? [{ label: 'Free months', value: String(r.free_months) }] : []),
            { label: 'Priced from', value: <PricedFrom explanation={r} env={env} tenantId={tenantId} /> },
          ]} />
        </div>
      )}
    </Section>
  );
}

/** Where a price came from, in words, with the page that holds it. */
function PricedFrom({ explanation: r, env, tenantId }: { explanation: PriceExplanation['resolution']; env: string; tenantId: string }) {
  if (r.override_id) {
    return (
      <span>
        A price override for this tenant <span className="mono t-caption muted">{shortId(r.override_id)}</span> ·{' '}
        <Link to={`/ops/tenants/${encodeURIComponent(env)}/${tenantId}?tab=billing`}>Billing tab</Link>
      </span>
    );
  }
  if (r.offer_id) {
    return <span>An offer <span className="mono t-caption muted">{shortId(r.offer_id)}</span> · <Link to={`/ops/offers?env=${encodeURIComponent(env)}`}>Offers</Link></span>;
  }
  if (r.price_book_version_id) {
    return <span>The published price book <span className="mono t-caption muted">{shortId(r.price_book_version_id)}</span> · <Link to={`/ops/pricing?env=${encodeURIComponent(env)}`}>Pricing</Link></span>;
  }
  return <span>The default price</span>;
}

export interface VolumeRow {
  product: ProjectionItem['product'];
  feature: string;
  category: string;
  country: string;
  quantity: string;
  unit: string;
}

const EMPTY_ROW: VolumeRow = { product: 'sms', feature: '', category: 'marketing', country: 'EG', quantity: '', unit: 'part' };

/**
 * The categories each priced unit takes (billing's cost-card vocabulary, C10): an SMS part is priced by PURPOSE, a
 * provider verification by CHANNEL, a WhatsApp message by Meta's CATEGORY. A category of one unit is meaningless for
 * another (an SMS "verification" has no "marketing" purpose), so changing the unit or the product resets it.
 */
export const CALCULATOR_CATEGORIES: Record<string, string[]> = {
  'sms:part': ['transactional', 'marketing', 'otp'],
  'sms:verification': ['sms', 'whatsapp'],
  'whatsapp:message': ['marketing', 'utility', 'authentication', 'service'],
};

export function calculatorCategories(product: string, unit: string): string[] {
  return CALCULATOR_CATEGORIES[`${product}:${product === 'whatsapp' ? 'message' : unit || 'part'}`] ?? [];
}

/** The row after a unit or product change: the category resets to the new unit's first one (never a stale one). */
export function withUnitOrProduct(row: VolumeRow, patch: Pick<Partial<VolumeRow>, 'product' | 'unit'>): VolumeRow {
  const product = patch.product ?? row.product;
  const unit = patch.product !== undefined && patch.product !== row.product
    ? (product === 'sms' ? 'part' : '')
    : patch.unit ?? row.unit;
  const categories = calculatorCategories(product, unit);
  const category = categories.includes(row.category) && product === row.product && unit === row.unit
    ? row.category
    : categories[0] ?? '';
  return { ...row, product, unit, category };
}

const PRODUCT_OPTIONS = [
  { value: 'ai', label: 'AI' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
  { value: 'payments', label: 'Payments' },
];

/** The rows as the API wants them — only the fields that product uses. */
export function toProjectionItems(rows: VolumeRow[]): { items: ProjectionItem[]; error: string | null } {
  const items: ProjectionItem[] = [];
  for (const [index, row] of rows.entries()) {
    const quantity = Number(row.quantity);
    // Billing's rating engine takes a positive whole quantity (parts, messages, requests).
    if (row.quantity.trim() === '' || !Number.isSafeInteger(quantity) || quantity <= 0) {
      return { items: [], error: `Row ${index + 1}: quantity must be a whole number above 0.` };
    }
    const item: ProjectionItem = { product: row.product, quantity };
    if (row.product === 'ai') {
      if (!row.feature.trim()) return { items: [], error: `Row ${index + 1}: an AI row needs a feature (translation, summarization, …).` };
      item.feature = row.feature.trim();
    }
    if (row.product === 'sms' || row.product === 'whatsapp') {
      item.category = row.category.trim() || undefined;
      item.country = row.country.trim().toUpperCase() || undefined;
    }
    if (row.product === 'sms' && row.unit) item.unit = row.unit;
    items.push(item);
  }
  return { items, error: null };
}

function ProjectionCalculator({ env, tenantId }: { env: string; tenantId: string }) {
  const [planCode, setPlanCode] = useState('');
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [seats, setSeats] = useState('');
  const [storageGb, setStorageGb] = useState('');
  const [rows, setRows] = useState<VolumeRow[]>([
    { ...EMPTY_ROW, quantity: '10000' },
    { product: 'whatsapp', feature: '', category: 'marketing', country: 'EG', quantity: '5000', unit: '' },
    { product: 'ai', feature: 'translation', category: '', country: '', quantity: '20000', unit: '' },
  ]);

  const { items, error } = toProjectionItems(rows);
  const seatsNumber = seats.trim() === '' ? undefined : Number(seats);
  const storageNumber = storageGb.trim() === '' ? undefined : Number(storageGb);
  const numbersOk =
    (seatsNumber === undefined || (Number.isInteger(seatsNumber) && seatsNumber >= 0)) &&
    (storageNumber === undefined || (Number.isFinite(storageNumber) && storageNumber >= 0));

  const project = useMutation({
    mutationFn: () => {
      const body: ProjectionBody = {
        plan_code: planCode.trim() || undefined,
        billing_period: billingPeriod,
        seats: seatsNumber,
        storage_gb: storageNumber,
        volumes: items,
      };
      return billingApi.project(env, tenantId, body);
    },
    gcTime: 0,
  });

  const update = (index: number, patch: Partial<VolumeRow>) =>
    setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const replace = (index: number, next: VolumeRow) => setRows(rows.map((r, i) => (i === index ? next : r)));

  return (
    <Section title="Projection calculator" meta="assumed volumes through the same line builder as an invoice">
      <div className="form-grid">
        <TextField label="Plan code" value={planCode} onChange={setPlanCode} mono hint="Empty = the tenant's current plan." />
        <Segmented label="Billing period" value={billingPeriod} onChange={setBillingPeriod}
          options={[{ value: 'monthly', label: 'Monthly' }, { value: 'annual', label: 'Annual' }]} />
        <TextField label="Seats" value={seats} onChange={setSeats} type="number" min={0} hint="Empty = current." />
        <TextField label="Storage (GB)" value={storageGb} onChange={setStorageGb} type="number" min={0} step="any" hint="Empty = current." />
      </div>

      <h3 className="t-heading" style={{ margin: '16px 0 8px' }}>Volumes per month</h3>
      <div className="stack-sm">
        {rows.map((row, index) => (
          <div className="row-end" key={index}>
            <div style={{ width: 140 }}>
              <Select label="Product" value={row.product}
                onChange={(v) => replace(index, withUnitOrProduct(row, { product: v as VolumeRow['product'] }))}
                options={PRODUCT_OPTIONS} searchable={false} />
            </div>
            {row.product === 'ai' && (
              <div style={{ width: 180 }}>
                <TextField label="Feature" value={row.feature} onChange={(v) => update(index, { feature: v })} mono placeholder="translation" />
              </div>
            )}
            {(row.product === 'sms' || row.product === 'whatsapp') && (
              <>
                <div style={{ width: 160 }}>
                  <Select label={row.product === 'sms' ? (row.unit === 'verification' ? 'Channel' : 'Purpose') : 'Category'}
                    value={row.category} onChange={(v) => update(index, { category: v })} searchable={false}
                    options={calculatorCategories(row.product, row.unit).map((c) => ({ value: c, label: c }))} />
                </div>
                <div style={{ width: 90 }}>
                  <TextField label="Country" value={row.country} onChange={(v) => update(index, { country: v })} mono placeholder="EG" />
                </div>
              </>
            )}
            {row.product === 'sms' && (
              <div style={{ width: 150 }}>
                <Select label="Unit" value={row.unit || 'part'} onChange={(v) => replace(index, withUnitOrProduct(row, { unit: v }))} searchable={false}
                  options={[{ value: 'part', label: 'part' }, { value: 'verification', label: 'verification' }]} />
              </div>
            )}
            <div style={{ width: 140 }}>
              <TextField label="Quantity" value={row.quantity} onChange={(v) => update(index, { quantity: v })} type="number" min={1} step={1} />
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRows(rows.filter((_, i) => i !== index))}>Remove</button>
          </div>
        ))}
      </div>
      <div className="form-actions" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRows([...rows, { ...EMPTY_ROW }])}>Add volume</button>
        <button type="button" className="btn btn-primary" disabled={!!error || !numbersOk || project.isPending} onClick={() => project.mutate()}>
          {project.isPending ? 'Projecting…' : 'Project'}
        </button>
        {error && <span className="t-caption note-error">{error}</span>}
      </div>
      <MutationError error={project.error} service="billing" />

      {project.data && (
        <div className="stack" style={{ marginTop: 16 }}>
          <RatedLinesTable usage={project.data.projection} caption="Projected lines" />
          <details className="viz-table" open>
            <summary>How each assumption was priced</summary>
            <table className="table">
              <thead><tr><th>Assumption</th><th className="num">Quantity</th><th className="num">Credits / unit</th><th className="num">Cost / unit</th><th>Why</th></tr></thead>
              <tbody>
                {project.data.assumptions.map((a, i) => (
                  <tr key={`${a.key}-${i}`}>
                    <td className="mono t-caption">{a.product} · {a.key}</td>
                    <td className="num">{formatNumber(a.quantity, 3)}</td>
                    <td className="num">{formatCredits(a.credits_per_unit, project.data!.projection.credit_display_scale)}</td>
                    <td className="num">{formatCredits(a.cost_credits_per_unit, project.data!.projection.credit_display_scale)}</td>
                    <td className="t-caption wrap">{a.explanation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      )}
    </Section>
  );
}
